/**
 * El service worker del app shell: registro, actualización y recuperación (P-65).
 *
 * Se registra sólo en build de producción: en `vite dev` un SW cacheando el
 * shell hace que los cambios no se vean y confunde el desarrollo.
 *
 * **La política de actualización, decidida en E3.7.** Un paciente nunca queda en
 * una versión vieja sin saberlo, y nunca le cambian la app debajo de los dedos:
 *
 *   - Cuando hay una versión nueva, el service worker nuevo **espera** (`sw.js`)
 *     y la app lo **avisa** con un botón "Actualizar" (`AvisoDeActualizacion`).
 *     Tocarlo le pide al nuevo que tome el control y recarga la página.
 *   - Si nadie toca nada, el nuevo toma el control solo la próxima vez que la app
 *     se abra desde cero: la versión vieja no sobrevive a cerrar la app.
 *   - La app pregunta si hay versión nueva cada vez que vuelve a primer plano y
 *     cada hora. Una PWA en el teléfono queda abierta en segundo plano por días:
 *     sin esto, el aviso llegaría tarde.
 *
 * Y si igual falla la carga de una pantalla —un chunk que ya no está en el
 * servidor—, `cargarConRecuperacion` recarga una vez contra la red, que trae el
 * shell nuevo con sus assets. Nunca una pantalla en blanco muda.
 */

export type RegisterOutcome = 'registered' | 'skipped-dev' | 'unsupported' | 'failed';

/** Lo mínimo de `ServiceWorker` que se usa, para poder probarlo sin navegador. */
export type Trabajador = {
  state: string;
  postMessage: (mensaje: unknown) => void;
  addEventListener: (tipo: 'statechange', escucha: () => void) => void;
};

export type Registro = {
  waiting: Trabajador | null;
  installing: Trabajador | null;
  update: () => Promise<unknown>;
  addEventListener: (tipo: 'updatefound', escucha: () => void) => void;
};

export type Contenedor = {
  controller: unknown;
  addEventListener: (tipo: 'controllerchange', escucha: () => void) => void;
};

export type ServiceWorkerSupport = {
  serviceWorker?: Contenedor & {
    register: (url: string, options?: { scope?: string }) => Promise<unknown>;
  };
};

export type RegisterOptions = {
  /** `import.meta.env.PROD` en la app; explícito para poder testearlo. */
  isProduction: boolean;
  navigator?: ServiceWorkerSupport;
  url?: string;
};

export async function registerServiceWorker({
  isProduction,
  navigator: nav = globalThis.navigator as ServiceWorkerSupport | undefined,
  url = '/sw.js',
}: RegisterOptions): Promise<RegisterOutcome> {
  if (!isProduction) return 'skipped-dev';
  if (!nav?.serviceWorker) return 'unsupported';

  try {
    const registro = await nav.serviceWorker.register(url, { scope: '/' });
    if (esRegistro(registro)) {
      vigilarActualizaciones({ registro, contenedor: nav.serviceWorker });
    }
    return 'registered';
  } catch (error) {
    // Que falle el SW no puede romper la app: se degrada a web sin offline.
    console.error('service_worker_register_failed', error);
    return 'failed';
  }
}

function esRegistro(valor: unknown): valor is Registro {
  return typeof valor === 'object' && valor !== null && 'update' in valor;
}

// ── La versión nueva ────────────────────────────────────────────────────────

let enEspera: Trabajador | null = null;
let pidioActualizar = false;
const escuchas = new Set<() => void>();

/** Hay un service worker nuevo esperando: la app tiene que ofrecer "Actualizar". */
export function hayVersionNueva(): boolean {
  return enEspera !== null;
}

export function suscribirseALaVersionNueva(escucha: () => void): () => void {
  escuchas.add(escucha);
  return () => escuchas.delete(escucha);
}

/** Lo que hace el botón "Actualizar": el nuevo toma el control y la página recarga. */
export function actualizarAhora(): void {
  if (!enEspera) return;
  pidioActualizar = true;
  enEspera.postMessage('SKIP_WAITING');
}

const UNA_HORA = 60 * 60 * 1000;

export function vigilarActualizaciones({
  registro,
  contenedor,
  documento = globalThis.document,
  recargar = () => globalThis.location.reload(),
  cadaCuanto = UNA_HORA,
}: {
  registro: Registro;
  contenedor: Contenedor;
  documento?: Pick<Document, 'visibilityState' | 'addEventListener'>;
  recargar?: () => void;
  cadaCuanto?: number;
}): void {
  const avisar = (trabajador: Trabajador) => {
    // Sin controlador es la primera instalación, no una versión nueva: no hay
    // nada viejo que reemplazar.
    if (!contenedor.controller) return;
    enEspera = trabajador;
    for (const escucha of escuchas) escucha();
  };

  if (registro.waiting) avisar(registro.waiting);

  registro.addEventListener('updatefound', () => {
    const nuevo = registro.installing;
    if (!nuevo) return;
    nuevo.addEventListener('statechange', () => {
      if (nuevo.state === 'installed') avisar(nuevo);
    });
  });

  // Recarga sólo si la persona lo pidió: el primer `clients.claim()` también
  // dispara este evento y no tiene por qué recargar nada.
  contenedor.addEventListener('controllerchange', () => {
    if (pidioActualizar) recargar();
  });

  const buscar = () => {
    registro.update().catch(() => undefined);
  };
  documento.addEventListener('visibilitychange', () => {
    if (documento.visibilityState === 'visible') buscar();
  });
  setInterval(buscar, cadaCuanto);
}

/** Sólo para los tests: el estado del módulo vuelve a cero. */
export function olvidarVersionNueva(): void {
  enEspera = null;
  pidioActualizar = false;
  escuchas.clear();
}

// ── La pantalla que no carga ────────────────────────────────────────────────

const MARCA_DE_RECARGA = 'pasos.recarga-por-version';
/** Si ya se recargó hace menos que esto, no se vuelve a recargar: se muestra el error. */
const VENTANA_DE_RECARGA = 30_000;

export type EntornoDeRecarga = {
  leer: () => string | null;
  guardar: (valor: string) => void;
  ahora: () => number;
  recargar: () => void;
};

function entornoDelNavegador(): EntornoDeRecarga {
  return {
    // sessionStorage puede tirar (modo privado, cookies bloqueadas): sin marca,
    // se recarga una vez y listo.
    leer: () => {
      try {
        return sessionStorage.getItem(MARCA_DE_RECARGA);
      } catch {
        return null;
      }
    },
    guardar: (valor) => {
      try {
        sessionStorage.setItem(MARCA_DE_RECARGA, valor);
      } catch {
        // Sin almacenamiento no hay marca; la ventana la cuida el navegador.
      }
    },
    ahora: () => Date.now(),
    recargar: () => globalThis.location.reload(),
  };
}

/**
 * Envuelve un `import()` de una pantalla: si el chunk no está —pasa después de
 * un deploy, con la versión vieja abierta—, recarga la página una vez contra la
 * red, que trae el shell nuevo. Si ya recargó hace un momento y sigue fallando,
 * deja pasar el error para que lo muestre `LimiteDeError`, con su "Reintentar":
 * recargar en loop sería otra forma de quedar atrapado.
 */
export function cargarConRecuperacion<T>(
  importar: () => Promise<T>,
  entorno: EntornoDeRecarga = entornoDelNavegador(),
): () => Promise<T> {
  return () =>
    importar().catch((error: unknown) => {
      const ultima = Number(entorno.leer() ?? 0);
      if (entorno.ahora() - ultima < VENTANA_DE_RECARGA) throw error;
      entorno.guardar(String(entorno.ahora()));
      entorno.recargar();
      // Mientras la página recarga, la pantalla sigue en su "cargando".
      return new Promise<T>(() => undefined);
    });
}
